// Remove OAuth handoffs from URLs before any telemetry can observe them.
import './lib/oauthLandingHandoff';

// Sentry must init before React renders so it captures all errors.
import './lib/sentry';

import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import './styles/index.css';
import { AuthProvider, authEnabled } from './lib/auth';
import { initPostHog, captureInstallLanded, capture } from './lib/analytics';
import { isNetworkError } from './lib/api/networkFailure';
import { installPageUnloadListeners } from './lib/api/pageUnloading';
import { installTranslateDomGuards } from '@overlayqa/shared/ui/translate-dom-guards';
import { AppErrorBoundary } from './components/AppErrorBoundary';
import { log } from '@/lib/logger';

// Initialize analytics as early as possible so pageview events
// are captured from the very first render.
initPostHog();
// A request the browser cancels because we navigated is not a failure. Install
// before the first render so the very first mount queries are covered: the
// 2026-08-20 case cancelled four of them 65ms after the dashboard rendered.
installPageUnloadListeners();
// Chrome Translate moves React-managed text nodes and crashed a real user's
// route transition on 2026-07-14 (white screen, no boundary). Install before
// the first render so every commit runs through the guards.
installTranslateDomGuards((operation, pageTranslated) => {
  log.warn('[translateDomGuards] Repaired a third-party DOM mutation', {
    operation,
    pageTranslated,
  });
  capture('dom_translate_guard_triggered', {
    operation,
    page_translated: pageTranslated,
  });
});
// Read install attribution before routing. Quiet extension-session routes are
// excluded; other explicit install landings require from=install plus an anon id.
captureInstallLanded();

if (!authEnabled) {
  log.warn('Missing VITE_CLERK_PUBLISHABLE_KEY - auth will not work');
}

const queryClient = new QueryClient({
  // Fires once per query, only after `retry` below is exhausted. A network blip
  // that healed on retry never reaches here; an outage the user actually hits
  // does. log.error is what mints the PostHog $exception (console-error
  // autocapture), so Error Tracking sees real failures and not laptop wakes.
  // Incident 2026-07-09. See lib/api/networkFailure.ts.
  queryCache: new QueryCache({
    onError: (error) => {
      if (isNetworkError(error)) {
        log.error('[api/client] Network failure surfaced to the user', {
          error: error.message,
        });
      }
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: 1,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <AppErrorBoundary>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </QueryClientProvider>
      </AuthProvider>
    </AppErrorBoundary>
  </React.StrictMode>
);
